import {useMutation, UseMutationResult, useQueryClient} from '@tanstack/react-query';
import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useNavigate} from 'react-router-dom';
import {toast} from 'react-toastify';
import {QUERY_KEYS} from '../../../../utils/queryKeys';
import {type TypedTranslation} from '../../../../i18n';
import Button from '../../../../incase-domain/components/button/Button';
import {FONT_SIZE, IPresentationTheme} from '../../../../incase-domain/presentationTheme';
import {createPresentationTheme} from '../../../../service/presentationTheme.service';
import {defaultFonts} from '../../../../utils/defaultFonts';
import PresentationThemeFavoriteColors from '../components/PresentationThemeFavoriteColors';
import PresentationThemeFonts from '../components/PresentationThemeFonts';
import PresentationThemeFontSizes from '../components/PresentationThemeFontSizes';
import PresentationThemeTextElement from '../components/PresentationThemeTextElement';
import PresentationThemeQuestionElement from '../components/questionElement/PresentationThemeQuestionElement';

function CreatePresentationTheme(): JSX.Element {
    const {t} = useTranslation();
    const client = useQueryClient();

    const [isMounted, setIsMounted] = useState(true);
    const [presentationTheme, setPresentationTheme] = useState<IPresentationTheme>({
        themeName: '',
        favoriteColors: [],
        fontSizes: [
            {size: FONT_SIZE.EXTRA_SMALL, value: 0.5},
            {size: FONT_SIZE.SMALL, value: 1},
            {size: FONT_SIZE.MEDIUM, value: 1.5},
            {size: FONT_SIZE.LARGE, value: 2},
            {size: FONT_SIZE.EXTRA_LARGE, value: 2.5},
        ],
        elementStyles: {
            textElement: {
                color: undefined,
                borderRadius: undefined,
                backgroundColor: undefined,
                fontSize: undefined,
            },
            questionElement: {
                questionFontSize: undefined,
                answerFontSize: undefined,
                commentFontSize: undefined,
            },
        },
        fonts: [],
        defaultFonts,
        lastModified: 0,
    });
    const navigate = useNavigate();

    const isDisabled = !presentationTheme.themeName || presentationTheme.themeName.trim().length === 0;

    const addPresentationTheme: UseMutationResult<
        IPresentationTheme,
        {
            message: TypedTranslation;
        },
        IPresentationTheme
    > = useMutation((theme) => createPresentationTheme(theme), {
        onSuccess: () =>
            client.invalidateQueries({queryKey: QUERY_KEYS.theme.lists()}).then(() => {
                toast.success(t('presentationThemesPage.toasts.presThemeCreateSuccess'));
                navigate('/dashboard');
            }),
        onError: (response) => {
            if (response.message.includes('tooManyThemes')) {
                toast.error(t(response.message));
            }
        },
    });

    useEffect(() => {
        return () => {
            setIsMounted(false);
        };
    }, []);

    return (
        <>
            <div className="u-flex u-h-between u-v-center l-grid l-grid--noGaps ">
                <div className="l-col:4">{t('presentationThemesPage.themeName')}:</div>
                <div className="l-col:4">
                    <input
                        type="text"
                        className="e-input"
                        placeholder={t('presentationThemesPage.themeName')}
                        maxLength={15}
                        value={presentationTheme.themeName}
                        onChange={(e) => {
                            e.preventDefault();
                            setPresentationTheme({...presentationTheme, themeName: e.target.value});
                        }}
                    />
                </div>
            </div>
            <PresentationThemeFavoriteColors
                favoriteColorsList={presentationTheme.favoriteColors}
                setPresentationTheme={setPresentationTheme}
                presentationTheme={presentationTheme}
            />
            <PresentationThemeFontSizes setPresentationTheme={setPresentationTheme} presentationTheme={presentationTheme} />
            <PresentationThemeFonts setPresentationTheme={setPresentationTheme} presentationTheme={presentationTheme} />
            <hr />
            <PresentationThemeTextElement presentationTheme={presentationTheme} setPresentationTheme={setPresentationTheme} />
            <hr />
            <PresentationThemeQuestionElement presentationTheme={presentationTheme} setPresentationTheme={setPresentationTheme} />

            <div className="u-flex u-gap-2" style={{justifyContent: 'right'}}>
                <Button
                    size="sm"
                    color="secondary"
                    variant="ghost"
                    onClick={() => {
                        navigate('/dashboard');
                    }}
                >
                    {t('common.cancel')}
                </Button>
                <Button
                    size="sm"
                    color="secondary"
                    variant="solid"
                    disabled={isDisabled}
                    onClick={() => {
                        if (isMounted) {
                            addPresentationTheme.mutate(presentationTheme);
                        }
                    }}
                >
                    {t('common.save')}
                </Button>
            </div>
        </>
    );
}

export default CreatePresentationTheme;
